import { format, parseISO } from 'date-fns';
import { AlertTriangle, Briefcase, Download, FileText, HandCoins, Loader2, Search, Settings } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AllocationHistoryModal } from '@/components/transactions/allocation-history-modal';
import { TransactionSendCopyInvoiceModal } from '@/components/transactions/transaction-send-copy-invoice-modal';
import { CreateCaseDrawer } from '@/components/collections/create-case-drawer';
import { CreatePromiseDrawer } from '@/components/collections/create-promise-drawer';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { ContextualCreateTaskDrawer } from '@/components/tasks/contextual-create-task-drawer';
import { SelectionToolbar } from '@/components/transactions/selection-toolbar';
import {
  AddNoteModal,
  CreateDisputeModal,
  type NoteFormData,
} from '@/components/transactions/transaction-action-modals';
import { TransactionDetailSheet } from '@/components/transactions/transaction-detail-sheet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCompany } from '@/contexts/CompanyContext';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useCustomerTransactionsPaginatedGraphQL } from '@/hooks/useTransactionsGraphQL';
import { useExportAll } from '@/hooks/useExportAll';
import { ExportProvider } from '@/providers/ExportProvider';
import { cn, formatDateDDMMYYYY } from '@/lib/utils';
import type { CustomerAgeingData } from '@/types/ageing-analysis';
import type { ColumnInput } from '@/types/export';
import type { Transaction } from '@/types/transaction';

interface TransactionsTabProps {
  customer: CustomerAgeingData;
  customerId?: string;
  formatCurrency: (amount: number, currency?: string) => string;
}

const DEFAULT_OUTSTANDING_TRANSACTIONS_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference #', visible: true, order: 0 },
  { id: 'description', label: 'Description', visible: true, order: 1 },
  { id: 'flags', label: 'Flags', visible: true, order: 2 },
  { id: 'date', label: 'Date', visible: true, order: 3 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 4 },
  { id: 'anticipatedDate', label: 'Anticipated Date', visible: true, order: 5 },
  { id: 'amount', label: 'Gross', visible: true, order: 6 },
  { id: 'outstanding', label: 'Outstanding', visible: true, order: 7 },
  { id: 'daysOverdue', label: 'Days Overdue', visible: true, order: 8 },
];

export function TransactionsTab(props: Readonly<TransactionsTabProps>) {
  // ExportProvider is required by useExportAll (server-side export), matching the Transactions screen
  return (
    <ExportProvider>
      <TransactionsTabInner {...props} />
    </ExportProvider>
  );
}

function TransactionsTabInner({ customer, customerId, formatCurrency }: Readonly<TransactionsTabProps>) {
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const baseCurrency = selectedCompany?.currency ?? 'GBP';

  // Real numeric customer id (falls back to the ageing row id) used to pre-populate action drawers
  const resolvedCustomerId = customerId ?? customer.id;

  // Format invoice amounts in the customer's own currency by default
  const formatInvoiceAmount = useCallback(
    (amount: number) => formatCurrency(amount, customer.currency),
    [formatCurrency, customer.currency],
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);

  // Transaction detail modal state
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // Create Collection Case modal state
  const [isCreateCaseModalOpen, setIsCreateCaseModalOpen] = useState(false);

  // Add Note modal state
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isCreateDisputeModalOpen, setIsCreateDisputeModalOpen] = useState(false);
  const [isSendCopyInvoiceModalOpen, setIsSendCopyInvoiceModalOpen] = useState(false);
  const [isRecordPromiseModalOpen, setIsRecordPromiseModalOpen] = useState(false);
  const [isAllocationHistoryModalOpen, setIsAllocationHistoryModalOpen] = useState(false);
  const [isCreateTaskDrawerOpen, setIsCreateTaskDrawerOpen] = useState(false);

  // Column preferences management
  const {
    columns: outstandingColumns,
    visibleColumns: visibleOutstandingColumns,
    savePreferences: saveOutstandingPreferences,
    resetToDefaults: resetOutstandingPreferences,
    columnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.AgeingAnalysisCustomerOutstandingTransactions,
    DEFAULT_OUTSTANDING_TRANSACTIONS_COLUMNS,
  );

  // Sorting state
  type SortField =
    | 'referenceNumber'
    | 'date'
    | 'dueDate'
    | 'anticipatedDate'
    | 'amount'
    | 'outstanding'
    | 'daysOverdue'
    | 'ageing';
  type SortDirection = 'asc' | 'desc' | null;
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Default column widths
  const defaultColumnWidths: Record<string, number> = {
    referenceNumber: 130,
    description: 180,
    flags: 90,
    date: 110,
    dueDate: 110,
    anticipatedDate: 130,
    amount: 120,
    outstanding: 120,
    daysOverdue: 110,
  };

  // Sortable field set
  const sortableFields = new Set([
    'referenceNumber',
    'date',
    'dueDate',
    'anticipatedDate',
    'amount',
    'outstanding',
    'daysOverdue',
  ]);

  // Selection state
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<Set<string>>(new Set());

  // Selection handlers
  const selectAll = () => {
    setSelectedTransactionIds(new Set(filteredInvoices.map((inv) => inv.id)));
  };

  const deselectAll = () => {
    setSelectedTransactionIds(new Set());
  };

  const selectTransaction = useCallback((transactionId: string) => {
    setSelectedTransactionIds((prev) => {
      const next = new Set(prev);
      next.add(transactionId);
      return next;
    });
  }, []);

  const deselectTransaction = useCallback((transactionId: string) => {
    setSelectedTransactionIds((prev) => {
      const next = new Set(prev);
      next.delete(transactionId);
      return next;
    });
  }, []);

  // Map-based handlers to satisfy SonarQube S2301 (no boolean branching)
  const selectAllActions = useMemo(
    () => ({
      true: selectAll,
      false: deselectAll,
    }),
    [selectAll, deselectAll],
  );

  const handleSelectAllToggle = useCallback(
    (checked: boolean) => selectAllActions[String(checked) as 'true' | 'false'](),
    [selectAllActions],
  );

  const handleSelectRowToggle = useCallback(
    (id: string | number, checked: boolean) => {
      const actions = { true: selectTransaction, false: deselectTransaction };
      actions[String(checked) as 'true' | 'false'](id as string);
    },
    [selectTransaction, deselectTransaction],
  );

  const handleClearSelection = () => {
    setSelectedTransactionIds(new Set());
  };

  // Action handlers
  const handleSendCopyInvoice = () => {
    setIsSendCopyInvoiceModalOpen(true);
  };

  const handleCreateCollectionCase = () => {
    setIsCreateCaseModalOpen(true);
  };

  const handleCreatePromise = () => {
    setIsRecordPromiseModalOpen(true);
  };

  const handleSavePromise = () => {
    toast.success('Promise to pay recorded successfully');
    setIsRecordPromiseModalOpen(false);
  };

  const handleCaseCreated = () => {
    console.log('Collection case created successfully');
    setIsCreateCaseModalOpen(false);
  };

  const handleCreateDispute = () => {
    setIsCreateDisputeModalOpen(true);
  };

  const handleAddNote = () => {
    setIsAddNoteModalOpen(true);
  };

  const handleSaveNote = (noteData: NoteFormData) => {
    console.log('Saving note:', noteData);
    toast.success('Note added successfully');
    setIsAddNoteModalOpen(false);
    // Clear selection after adding note
    setSelectedTransactionIds(new Set());
  };

  // Convert OutstandingInvoice to Transaction (fallback when the source transaction can't be resolved)
  const convertInvoiceToTransaction = (invoice: (typeof outstandingInvoices)[0]): Transaction => {
    return {
      id: parseInt(invoice.id) || 0,
      referenceNumber: invoice.invoiceNumber,
      headerReference: invoice.invoiceNumber,
      transactionDate: invoice.date,
      customerId: Number(resolvedCustomerId) || customer.customerId || 0,
      customerName: customer.customerName,
      accountNumber: customer.accountNumber,
      transactionType: invoice.transactionType ?? 'invoice',
      amount: invoice.amount,
      currency: customer.currency,
      status: invoice.daysOverdue > 0 ? 'overdue' : 'outstanding',
      description: invoice.description || `Invoice ${invoice.invoiceNumber}`,
      createdBy: 'System',
      createdAt: invoice.date,
      dueDate: invoice.dueDate,
      anticipatedDate: invoice.anticipatedDate,
      outstandingAmount: invoice.outstanding,
      onDispute: invoice.flags?.isDisputed,
    };
  };

  // Handle opening transaction detail modal
  const handleOpenTransactionDetail = (invoice: (typeof outstandingInvoices)[0]) => {
    const transaction = convertInvoiceToTransaction(invoice);
    setSelectedTransaction(transaction);
    setIsTransactionModalOpen(true);
  };

  // Handle column sorting - cycles asc -> desc -> clear
  const handleSort = (field: string) => {
    const typedField = field as SortField;
    if (sortField === typedField) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      setSortField(typedField);
      setSortDirection('asc');
    }
  };

  const getOverdueColor = (daysOverdue: number) => {
    if (daysOverdue <= 0) return 'text-green-600';
    if (daysOverdue <= 30) return 'text-green-600';
    if (daysOverdue <= 60) return 'text-yellow-600';
    if (daysOverdue <= 90) return 'text-orange-600';
    if (daysOverdue <= 120) return 'text-red-600';
    return 'text-red-700';
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDateDDMMYYYY(dateString);
    } catch {
      return dateString;
    }
  };

  // Map UI sort field names to sortFieldMap keys used by useCustomerTransactionsPaginatedGraphQL
  const sortFieldToGraphQL: Record<string, string> = {
    referenceNumber: 'referenceNumber',
    date: 'transactionDate',
    dueDate: 'dueDate',
    anticipatedDate: 'anticipatedDate',
    amount: 'amount',
    outstanding: 'outstandingAmount',
    daysOverdue: 'daysOverdue',
  };

  const graphQLSortBy =
    sortField && sortDirection ? (sortFieldToGraphQL[sortField] ?? 'transactionDate') : 'transactionDate';
  const graphQLSortOrder = sortDirection ?? 'desc';

  const {
    transactions: liveTransactions,
    pagination: livePagination,
    isLoading,
  } = useCustomerTransactionsPaginatedGraphQL(customerId, {
    page: currentPage,
    pageSize,
    sortBy: graphQLSortBy,
    sortOrder: graphQLSortOrder,
    outstandingStatus: 'outstanding',
  });

  // Map Transaction objects to OutstandingInvoice format for the existing table UI
  const outstandingInvoices = useMemo(() => {
    return liveTransactions.map((tx) => ({
      id: String(tx.id),
      invoiceNumber: tx.referenceNumber,
      date: tx.transactionDate,
      dueDate: tx.dueDate ?? tx.transactionDate,
      anticipatedDate: tx.anticipatedDate,
      amount: tx.amount,
      outstanding: tx.outstandingAmount ?? 0,
      daysOverdue: tx.daysOverdue ?? 0,
      ageingBucket: tx.ageingBucket ?? 'current',
      description: tx.description,
      transactionType: tx.transactionType,
      flags: {
        isDisputed: tx.onDispute ?? tx.hasDispute,
        hasPromiseToPay: tx.hasPromise,
        isInCollectionCase: tx.hasCase,
      },
    }));
  }, [liveTransactions]);

  // Filter invoices based on search query and ageing bucket filter (client-side on current page)
  const filteredInvoices = useMemo(() => {
    return outstandingInvoices.filter((invoice) => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !query ||
        invoice.invoiceNumber.toLowerCase().includes(query) ||
        invoice.amount.toString().includes(query) ||
        invoice.outstanding.toString().includes(query) ||
        formatDate(invoice.date).toLowerCase().includes(query) ||
        formatDate(invoice.dueDate).toLowerCase().includes(query);
      return matchesSearch;
    });
  }, [outstandingInvoices, searchQuery]);

  const totalRecords = livePagination?.totalItems ?? 0;
  const totalPages = livePagination?.totalPages || Math.ceil(totalRecords / pageSize);

  // Convert visible columns to DataTableColumn format
  const dataTableColumns: DataTableColumn[] = useMemo(() => {
    return visibleOutstandingColumns.map((col) => ({
      id: col.id,
      label: col.label,
      sortKey: sortableFields.has(col.id) ? col.id : undefined,
      align: col.id === 'amount' || col.id === 'outstanding' ? ('right' as const) : undefined,
      defaultWidth: defaultColumnWidths[col.id] ?? 120,
      truncate: col.id === 'description',
    }));
  }, [visibleOutstandingColumns]);

  // Render cell content for each column
  const renderCell = useCallback(
    (columnId: string, invoice: (typeof outstandingInvoices)[0]) => {
      switch (columnId) {
        case 'referenceNumber':
          return <span className="text-left font-medium">{invoice.invoiceNumber}</span>;
        case 'description':
          return <span>{invoice.description || '-'}</span>;
        case 'flags':
          return (
            <TooltipProvider>
              <div className="flex items-center gap-2">
                {invoice.flags?.isDisputed && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 transition-colors cursor-help">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-red-600 text-white border-red-700">
                      <p className="font-semibold">Disputed</p>
                      <p className="text-xs">This transaction is under dispute</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {invoice.flags?.hasPromiseToPay && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors cursor-help">
                        <HandCoins className="h-4 w-4 text-blue-600" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-blue-600 text-white border-blue-700">
                      <p className="font-semibold">Promise to Pay</p>
                      <p className="text-xs">Customer has committed to payment</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {invoice.flags?.isInCollectionCase && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 hover:bg-orange-200 transition-colors cursor-help">
                        <Briefcase className="h-4 w-4 text-orange-600" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-orange-600 text-white border-orange-700">
                      <p className="font-semibold">Collection Case</p>
                      <p className="text-xs">Part of an active collection case</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {!invoice.flags?.isDisputed &&
                  !invoice.flags?.hasPromiseToPay &&
                  !invoice.flags?.isInCollectionCase && <span className="text-muted-foreground text-xs">-</span>}
              </div>
            </TooltipProvider>
          );
        case 'date':
          return <span>{formatDate(invoice.date)}</span>;
        case 'dueDate':
          return <span>{formatDate(invoice.dueDate)}</span>;
        case 'anticipatedDate':
          return <span>{invoice.anticipatedDate ? formatDate(invoice.anticipatedDate) : '-'}</span>;
        case 'amount':
          return <span className="font-medium">{formatInvoiceAmount(invoice.amount)}</span>;
        case 'outstanding':
          return <span className="font-semibold">{formatInvoiceAmount(invoice.outstanding)}</span>;
        case 'daysOverdue':
          return (
            <span className={cn('font-semibold', getOverdueColor(invoice.daysOverdue))}>
              {invoice.daysOverdue > 0 ? `${invoice.daysOverdue} days` : 'Current'}
            </span>
          );
        default:
          return null;
      }
    },
    [formatInvoiceAmount, formatDate, getOverdueColor, handleOpenTransactionDetail],
  );

  // Calculate selected transactions totals
  const selectedTransactions = useMemo(() => {
    return filteredInvoices.filter((inv) => selectedTransactionIds.has(inv.id));
  }, [filteredInvoices, selectedTransactionIds]);

  const selectedTotals = useMemo(() => {
    const totalAmount = selectedTransactions.reduce((sum, inv) => sum + inv.amount, 0);
    const totalOutstanding = selectedTransactions.reduce((sum, inv) => sum + inv.outstanding, 0);
    return {
      totalAmount,
      totalOutstanding,
      currency: customer.currency,
    };
  }, [selectedTransactions, customer.currency]);

  // Map real GraphQL transactions by id so actions receive the true id/customerId/accountNumber
  const transactionsById = useMemo(() => {
    const map = new Map<string, Transaction>();
    for (const tx of liveTransactions) {
      map.set(String(tx.id), tx);
    }
    return map;
  }, [liveTransactions]);

  // Convert selected invoices to Transaction[] for SelectionToolbar and the action modals,
  // preferring the real transaction so downstream API calls use correct ids.
  const selectedTransactionsAsTransaction = useMemo(() => {
    return selectedTransactions.map(
      (invoice) => transactionsById.get(invoice.id) ?? convertInvoiceToTransaction(invoice),
    );
  }, [selectedTransactions, transactionsById]);

  // Check if all filtered invoices are selected
  const allSelected = filteredInvoices.length > 0 && selectedTransactionIds.size === filteredInvoices.length;

  // Navigate to the allocate screen for the first selected unallocated receipt (mirrors the Transactions screen)
  const handleNavigateToAllocate = useCallback(() => {
    const unallocatedReceipts = selectedTransactionsAsTransaction.filter(
      (t) => t.transactionType === 'receipt' && t.outstandingAmount !== undefined && t.outstandingAmount !== 0,
    );
    if (unallocatedReceipts.length === 0) {
      toast.error('No unallocated receipts selected');
      return;
    }
    navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
  }, [selectedTransactionsAsTransaction, navigate]);

  const handleViewAllocationHistory = useCallback(() => {
    setIsAllocationHistoryModalOpen(true);
  }, []);

  // Build CSV content for a set of invoice rows and columns
  const exportInvoicesToCsv = useCallback(
    (rows: typeof filteredInvoices, columns: ColumnDefinition[], fileSuffix: string) => {
      if (rows.length === 0) {
        toast.info('No transactions to export');
        return;
      }
      const header = columns.map((col) => `"${col.label}"`).join(',');
      const csvRows = rows.map((invoice) =>
        columns
          .map((col) => {
            let value = '';
            switch (col.id) {
              case 'referenceNumber':
                value = invoice.invoiceNumber;
                break;
              case 'description':
                value = invoice.description || '';
                break;
              case 'flags': {
                const flags: string[] = [];
                if (invoice.flags?.isDisputed) flags.push('Disputed');
                if (invoice.flags?.hasPromiseToPay) flags.push('Promise to Pay');
                if (invoice.flags?.isInCollectionCase) flags.push('Collection Case');
                value = flags.join(', ');
                break;
              }
              case 'date':
                value = formatDate(invoice.date);
                break;
              case 'dueDate':
                value = formatDate(invoice.dueDate);
                break;
              case 'anticipatedDate':
                value = invoice.anticipatedDate ? formatDate(invoice.anticipatedDate) : '';
                break;
              case 'amount':
                value = String(invoice.amount);
                break;
              case 'outstanding':
                value = String(invoice.outstanding);
                break;
              case 'daysOverdue':
                value = invoice.daysOverdue > 0 ? String(invoice.daysOverdue) : '0';
                break;
              default:
                break;
            }
            return `"${value.replace(/"/g, '""')}"`;
          })
          .join(','),
      );
      const csvContent = [header, ...csvRows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${customer.customerName.replace(/[^a-zA-Z0-9]/g, '_')}_transactions${fileSuffix}_${format(new Date(), 'yyyyMMdd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Exported ${rows.length} transactions to CSV`);
    },
    [formatDate, customer.customerName],
  );

  const handleExportCsv = useCallback(
    () => exportInvoicesToCsv(filteredInvoices, visibleOutstandingColumns, ''),
    [exportInvoicesToCsv, filteredInvoices, visibleOutstandingColumns],
  );

  // Server-side export of the selected transactions (mirrors the Transactions screen: Transactions form,
  // filtered by the selected transaction ids, delivered asynchronously via the export job service).
  const transactionExportColumnNameMap = useMemo<Record<string, string>>(
    () => ({
      referenceNumber: 'ReferenceNumber',
      description: 'Description',
      flags: 'Flags',
      date: 'TransactionDate',
      dueDate: 'DueDate',
      anticipatedDate: 'AnticipatedDate',
      amount: 'DisplayAmount',
      outstanding: 'DisplayOutstandingAmount',
    }),
    [],
  );

  const getSelectedExportFilter = useCallback((): Record<string, unknown> | undefined => {
    const ids = selectedTransactionsAsTransaction
      .map((t) => t.id)
      .filter((id): id is number => Number.isFinite(id) && id > 0);
    if (ids.length === 0) return undefined;
    return { transactionId: { containsValues: ids } };
  }, [selectedTransactionsAsTransaction]);

  const getSelectedExportCurrentViewColumns = useCallback(
    (): ColumnInput[] =>
      visibleOutstandingColumns
        .filter((col) => col.id in transactionExportColumnNameMap)
        .map((col) => ({ name: transactionExportColumnNameMap[col.id], displayName: col.label })),
    [visibleOutstandingColumns, transactionExportColumnNameMap],
  );

  const getSelectedExportAllColumns = useCallback(
    (): ColumnInput[] => [
      { name: 'ReferenceNumber', displayName: 'Reference Number' },
      { name: 'Flags', displayName: 'Flags' },
      { name: 'TransactionDate', displayName: 'Transaction Date' },
      { name: 'DueDate', displayName: 'Due Date' },
      { name: 'AnticipatedDate', displayName: 'Anticipated Date' },
      { name: 'TransactionTypeName', displayName: 'Type' },
      { name: 'CurrencyCode', displayName: 'Currency' },
      { name: 'DisplayAmount', displayName: 'Gross' },
      { name: 'DisplayOutstandingAmount', displayName: 'Outstanding' },
      { name: 'TransactionStatusCode', displayName: 'Status' },
      { name: 'Description', displayName: 'Description' },
      { name: 'OnDispute', displayName: 'External Dispute' },
    ],
    [],
  );

  const selectedExportFileName = `selected-outstanding-transactions-${customer.customerName || 'customer'}-${new Date().toISOString().split('T')[0]}`;

  const { triggerExportAll: triggerSelectedCurrentView } = useExportAll({
    form: 'Transactions',
    getColumns: getSelectedExportCurrentViewColumns,
    getFilter: getSelectedExportFilter,
    fileName: selectedExportFileName,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerSelectedAllColumns } = useExportAll({
    form: 'Transactions',
    getColumns: getSelectedExportAllColumns,
    getFilter: getSelectedExportFilter,
    fileName: `${selectedExportFileName}-all-columns`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const handleExportSelectedCurrentView = useCallback(() => {
    triggerSelectedCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerSelectedCurrentView]);

  const handleExportSelectedAllColumns = useCallback(() => {
    triggerSelectedAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerSelectedAllColumns]);

  return (
    <div className="relative h-full">
      <ScrollArea className="h-full">
        <div className="space-y-6">
          {/* Transactions Table */}
          <Card className="flex flex-col min-h-0">
            <CardHeader className="pb-2 flex-none">
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-base whitespace-nowrap">Outstanding Transactions ({totalRecords})</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative w-56">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          onClick={handleExportCsv}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>Export to CSV</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          onClick={() => setIsColumnConfigOpen(true)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>Configure Columns</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 flex flex-col min-h-0 flex-1 p-0">
              <DataTable
                columns={dataTableColumns}
                data={filteredInvoices}
                getRowId={(invoice) => invoice.id}
                renderCell={renderCell}
                sortField={sortField ?? undefined}
                sortDirection={sortDirection ?? undefined}
                onSort={handleSort}
                showCheckbox
                selectedIds={selectedTransactionIds}
                allSelected={allSelected}
                onSelectAll={handleSelectAllToggle}
                onSelectRow={handleSelectRowToggle}
                isLoading={isLoading}
                initialColumnOrder={columnOrder}
                initialColumnWidths={columnWidths}
                initialPinnedLeft={pinnedLeft}
                initialPinnedRight={pinnedRight}
                onColumnOrderChange={setColumnOrder}
                onColumnWidthsChange={setColumnWidths}
                onPinnedChange={(left, right) => setPinned(left, right)}
                onResetColumnOrder={resetColumnOrder}
                onResetColumnWidths={resetColumnWidths}
                loadingContent={
                  <div className="flex flex-col items-center justify-center space-y-4 py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Loading transactions...</p>
                  </div>
                }
                emptyContent={
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-16 w-16 mx-auto mb-4 opacity-20" />
                    {outstandingInvoices.length === 0 ? (
                      <>
                        <p className="text-sm font-medium">No Outstanding Transactions</p>
                        <p className="text-xs mt-1">This customer has no outstanding invoices</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium">No Matching Transactions</p>
                        <p className="text-xs mt-1">Try adjusting your search query</p>
                      </>
                    )}
                  </div>
                }
                pagination={{
                  currentPage,
                  totalPages: totalPages || 1,
                  pageSize,
                  totalItems: totalRecords,
                  onPageChange: setCurrentPage,
                  onPageSizeChange: (size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  },
                  pageSizeOptions: [5, 10, 25, 50, 200],
                }}
              />
            </CardContent>
          </Card>
        </div>
      </ScrollArea>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        columns={outstandingColumns}
        onApply={saveOutstandingPreferences}
        onReset={resetOutstandingPreferences}
      />

      {/* Transaction Detail Sheet */}
      <TransactionDetailSheet
        open={isTransactionModalOpen}
        onOpenChange={setIsTransactionModalOpen}
        transaction={selectedTransaction}
      />

      {/* Create Promise Drawer */}
      <CreatePromiseDrawer
        open={isRecordPromiseModalOpen}
        onOpenChange={setIsRecordPromiseModalOpen}
        onSave={handleSavePromise}
        triggerSource="action-bar"
        prePopulatedCustomer={{
          customerId: resolvedCustomerId,
          customerName: customer.customerName,
          accountNumber: customer.accountNumber,
        }}
        prePopulatedTransactions={
          selectedTransactions.length > 0
            ? selectedTransactions.map((t) => ({
                id: t.id,
                headerReference: t.invoiceNumber,
                invoiceDate: new Date(t.date),
                originalAmount: t.amount,
                balanceRemaining: t.outstanding,
                daysOverdue: t.daysOverdue,
              }))
            : undefined
        }
      />

      {/* Create Collection Case Drawer */}
      <CreateCaseDrawer
        open={isCreateCaseModalOpen}
        onOpenChange={setIsCreateCaseModalOpen}
        onCaseCreated={handleCaseCreated}
        prePopulatedCustomer={{
          customerId: resolvedCustomerId,
          customerName: customer.customerName,
          accountNumber: customer.accountNumber,
        }}
        selectedTransactions={selectedTransactionsAsTransaction}
        triggerSource="customer-transactions"
      />

      {/* Create Dispute Modal */}
      <CreateDisputeModal
        open={isCreateDisputeModalOpen}
        onOpenChange={setIsCreateDisputeModalOpen}
        selectedTransactions={selectedTransactionsAsTransaction}
        onSave={() => {
          setIsCreateDisputeModalOpen(false);
          setSelectedTransactionIds(new Set());
        }}
      />

      {/* Send Copy Invoice Modal */}
      <TransactionSendCopyInvoiceModal
        open={isSendCopyInvoiceModalOpen}
        onOpenChange={setIsSendCopyInvoiceModalOpen}
        transactions={selectedTransactionsAsTransaction}
        onSuccess={() => {
          setIsSendCopyInvoiceModalOpen(false);
          setSelectedTransactionIds(new Set());
        }}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        open={isAddNoteModalOpen}
        onOpenChange={setIsAddNoteModalOpen}
        selectedTransactions={selectedTransactionsAsTransaction}
        onSave={handleSaveNote}
      />

      {/* Floating Action Panel - SelectionToolbar */}
      {selectedTransactionIds.size > 0 && (
        <SelectionToolbar
          selectedCount={selectedTransactionIds.size}
          totals={{
            count: selectedTransactionIds.size,
            totalAmount: selectedTotals.totalAmount,
            totalOutstanding: selectedTotals.totalOutstanding,
            currency: selectedTotals.currency,
          }}
          selectedTransactions={selectedTransactionsAsTransaction}
          baseCurrency={baseCurrency}
          onClearSelection={handleClearSelection}
          onExportCurrentView={handleExportSelectedCurrentView}
          onExportAllColumns={handleExportSelectedAllColumns}
          onSendReminders={() => toast.success('Sending reminders...')}
          onAllocateReceipt={handleNavigateToAllocate}
          onSendCopyInvoice={handleSendCopyInvoice}
          onCreateCollectionCase={handleCreateCollectionCase}
          onCreatePromise={handleCreatePromise}
          onCreateDispute={handleCreateDispute}
          onAddNote={handleAddNote}
          onViewAllocationHistory={handleViewAllocationHistory}
          onCreateTask={() => setIsCreateTaskDrawerOpen(true)}
        />
      )}

      {/* Create Task Drawer - keeps the Customer Details drawer open behind it */}
      {selectedTransactionsAsTransaction.length > 0 && (
        <ContextualCreateTaskDrawer
          open={isCreateTaskDrawerOpen}
          onOpenChange={setIsCreateTaskDrawerOpen}
          customer={{
            id: String(resolvedCustomerId),
            name: customer.customerName,
            accountNumber: customer.accountNumber,
          }}
          transactions={selectedTransactionsAsTransaction}
          baseCurrency={baseCurrency}
          onCreated={() => setIsCreateTaskDrawerOpen(false)}
        />
      )}

      {/* Allocation History Modal */}
      <AllocationHistoryModal
        open={isAllocationHistoryModalOpen}
        onOpenChange={setIsAllocationHistoryModalOpen}
        transactionId={selectedTransactionsAsTransaction.length > 0 ? selectedTransactionsAsTransaction[0].id : null}
      />
    </div>
  );
}
